import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { thesisPillars, thesisScopeNote, thesisStatement } from '../../data/deckSlide17';
import { readDeckAudience } from '../../data/investorAccess';
import { useSlideNumber } from '../../components/deck/slideNumber';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
type Stage = 0 | 1 | 2 | 3;
const PILLAR_W = 320;
const PILLAR_GAP = 24;
const PILLAR_LEFT = 112;
export function InvestmentThesisSlide() {
  const [stage, setStage] = useState<Stage>(0);
  const isMarketing = readDeckAudience() === 'marketing';
  const slideNumber = useSlideNumber();
  useEffect(() => {
    const timers: number[] = [];
    const at = (ms: number, next: Stage) => timers.push(window.setTimeout(() => setStage(next), ms));
    at(800, 1); // pillars
    at(2600, 2); // closing statement
    at(3900, 3); // raise reminder
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  return <SlideStage data-id="element-2354" data-mp="1t1dejpb5nf9u">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2355" data-mp="1y2sxnbtrvbpl">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 65% at 50% -20%, rgba(0,200,83,0.07) 0%, rgba(250,252,249,0) 60%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2356" data-mp="1y0zrdxn2mti4" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2357" data-mp="2gtk6mc9ul03g">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2358" data-mp="mgub5q3tzity" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2359" data-mp="rdksegrdgkgy">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2360" data-mp="jdy8h5q9n7dn">
          {slideNumber || '17'} · Investment Thesis
        </motion.span>

        {/* Headline */}
        <h1 className="absolute text-[42px] font-semibold leading-[1.16] tracking-[-0.022em] text-[#0B1712]" style={{
        left: 112,
        top: 130,
        width: 1000
      }} data-id="element-2361" data-mp="12azhd1mxxrmv">
          {['AI communication is becoming a new layer.', 'VEXU is building it.'].map((line, i) => <motion.span key={line} className="block" initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.15 + i * 0.16,
          ease
        }} data-id="element-2362" data-mp="175lh27jntvr4" data-mpi={i}>
              {line}
            </motion.span>)}
        </h1>
        <motion.p className="absolute text-[16px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 252,
        width: 860
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.7,
        ease
      }} data-id="element-2363" data-mp="24pbwgp907tg8">
          One proprietary communication platform designed to serve individuals,
          businesses and new vertical markets.
        </motion.p>

        {/* Pillars */}
        {thesisPillars.map((pillar, i) => <motion.div key={pillar.index} className="absolute border-t border-[#0B1712]/[0.09] pt-5" style={{
        left: PILLAR_LEFT + i * (PILLAR_W + PILLAR_GAP),
        top: 348,
        width: PILLAR_W
      }} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: shown(1) ? 1 : 0,
        y: shown(1) ? 0 : 12
      }} transition={{
        duration: 0.8,
        delay: i * 0.16,
        ease
      }} data-id="element-2364" data-mp="17z2wd2s5ufpu" data-mpi={i}>
            <span className="text-[10.5px] font-semibold tracking-[0.22em] text-vexu-700/60 tabular-nums" data-id="element-2365" data-mp="21baqjbdwj16e">
              {pillar.index}
            </span>
            <p className="mt-2.5 text-[16px] font-semibold uppercase leading-snug tracking-[0.1em] text-[#0B1712]" data-id="element-2366" data-mp="1wizske2q1pgh">
              {pillar.title}
            </p>
            {pillar.lead && <p className="mt-3 text-[12px] leading-snug text-vexu-700" data-id="element-2367" data-mp="1mn65i0rdmdkg">
                {pillar.lead}
              </p>}
            <p className="mt-3 text-[13.5px] leading-snug text-[#5A6B60]" data-id="element-2368" data-mp="1in5tsnvnwgh3">
              {pillar.detail}
            </p>
          </motion.div>)}

        {/* Closing statement */}
        <motion.div className="absolute text-center" style={{
        left: 112,
        right: 112,
        top: 606
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(2) ? 1 : 0,
        y: shown(2) ? 0 : 10
      }} transition={{
        duration: 1,
        ease
      }} data-id="element-2369" data-mp="18ogcoxblb01w">
          <p className="text-[34px] font-semibold tracking-[-0.025em] text-[#0B1712]" data-id="element-2370" data-mp="3ajmdwkmga6mj">
            {thesisStatement}
          </p>
          <p className="mx-auto mt-4 max-w-[820px] text-[13px] leading-relaxed text-[#8A998F]" data-id="element-2371" data-mp="3fgw4w4phmqch">
            {thesisScopeNote}
          </p>
        </motion.div>

        {/* Raise reminder */}
        <motion.div className="absolute flex items-end justify-between gap-12 border-t border-[#0B1712]/[0.09] pt-6" style={{
        left: 112,
        right: 112,
        bottom: 52
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(3) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2372" data-mp="3pawf2kzmmls2">
          <div data-id="element-2373" data-mp="36iak02wjmz8j">
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-vexu-700" data-id="element-2374" data-mp="36iu3vp5r7xp7">
              Patent pending
            </p>
            <p className="mt-2 max-w-[560px] text-[12.5px] leading-snug text-[#8A998F]" data-id="element-2375" data-mp="gsysbhjxbz7k">
              IP protection around key elements of VEXU&rsquo;s communication
              architecture and interaction model.
            </p>
          </div>
          {!isMarketing && <div className="flex shrink-0 items-baseline gap-5" data-id="element-2376" data-mp="2cvljlph93h4e">
              <span className="text-[11px] font-semibold uppercase tracking-[0.26em] text-[#A3AFA7]" data-id="element-2377" data-mp="2qwo4w00f8ne9">
                Raising
              </span>
              <span className="text-[30px] font-semibold tracking-[-0.03em] text-[#0B1712] tabular-nums" data-id="element-2378" data-mp="2mvcaziailg09">
                $1.5M
              </span>
              <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-vexu-700" data-id="element-2379" data-mp="1u6f56enhpboe">
                SAFE
              </span>
            </div>}
        </motion.div>
      </div>
    </SlideStage>;
}